/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, shareReplay, take, takeUntil, tap } from 'rxjs/operators';

import { Datasource, IDatasource } from 'ngx-ui-scroll';

import { TooltipDirective } from 'shared';
import { isArrayNullOrEmpty, isElementNull } from '../../../../utils';
import {
  EDeviceErrorType,
  EMapContextMenuType,
  EPermissionStatus,
  EPlotIcon,
  EPlotType,
  ESignalStatus,
  EStatusFilterType,
  ETrackingDeviceEventCode,
  EVehicleDriverPanelTabs,
  EVehicleFuelType,
  EVehicleStatusType,
  IAccountSettings,
  IAssetPlot,
  IIndexableObject,
  IMapContextMenuData,
  IPoint,
  IScrollItem,
  ISignalStatusUpdate,
  IStatusPanelItem,
  IUserPermissions,
  IUserSettings,
  IVehiclePlot,
  IZoomToMarkerParams,
  LatLng,
  MarkerInfo
} from '../../../core/models';
import { DomHelperService } from '../../../core/services/dom-helper';
import {
  HighlightMarker,
  OpenVehicleContextMenu,
  RemoveMarkerHighlight,
  SetSidePanelSelectedItem,
  TryUpdateZoomToMarker,
  UpdateVehicleDriverPanelActiveTab,
  UpdateVehiclesStatus
} from '../../../core/store/actions';
import { RetrySearchAssignments } from '../../../core/store/actions/attribute.actions';
import { SelectVehicleIdForDeviceHealth } from '../../../core/store/actions/device-health.actions';
import {
  getAssignmentsError,
  getFilteredEntityIds,
  getStatusPanelPlots,
  getUserAccountSettings,
  getUserPermissions,
  getUserSettings,
  getVehicleDriverPanelActiveTab,
  getVehicleFuelTypesFromHierarchy,
  getVehiclePlots,
  getVehiclePlotsTimers,
  getVehiclesIdsSorted,
  getVehicleStatusFilterType,
  statusToFilterableStatusMap
} from '../../../core/store/selectors';
import { getAllDevicesHealth } from '../../../core/store/selectors/device-health.selector';
import { IAppState } from '../../../core/store/state';
import { IDeviceHealth } from '../../../core/store/state/device-health.state';
import { filterByEntityIds } from '../../../core/utils';
import { ContextMenuDataLoaderService } from '../../../maps/services/context-menu-data-loader';
import { ReverseGeocodingService } from '../../../services';
import { IVehicleStatusInfo } from '../../models';

@Component({
  selector: 'fm-vehicle-status',
  templateUrl: './vehicle-status.component.html',
  styleUrls: ['./vehicle-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VehicleStatusComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('normalSignalIcon', { read: TooltipDirective })
  public normalSignalIconTooltip: TooltipDirective;
  @ViewChild('lowSignalIcon', { read: TooltipDirective })
  public lowSignalIconTooltip: TooltipDirective;
  @ViewChild('lostSignalIcon', { read: TooltipDirective })
  public lostSignalIconTooltip: TooltipDirective;

  @Input()
  public hasFleetConnected: boolean;
  @Input()
  public hasFleetSelected: boolean;
  @Input()
  isStatusPlotsLoaded: boolean;
  @Input()
  itemToScrollTo: IScrollItem;
  @Input()
  isPingMVPEnabled: boolean;

  showSkeleton = input(false);

  @Output()
  public clicked: EventEmitter<void> = new EventEmitter<void>();

  public vehicles$: Observable<IVehiclePlot[]>;
  public userPermissions$: Observable<IUserPermissions>;
  public userAccountSettings$: Observable<IAccountSettings>;
  public userSettings$: Observable<IUserSettings>;
  public vehiclesIdsSorted$: Observable<number[]>;
  public vehicleStatusFilterSelected$: Observable<EStatusFilterType>;
  public vehicleStatusInfo$: Observable<IVehicleStatusInfo>;
  public vehiclePlotsTimers$: Observable<IIndexableObject<number>>;
  public vehicleFuelTypes$: Observable<Map<number, EVehicleFuelType>>;
  public isPermissionEnabled: EPermissionStatus;
  public allVehicleDeviceHealthStatus$: Observable<IDeviceHealth[]>;

  public statusPanelItems$: Observable<IStatusPanelItem[]>;
  public statusPanelItems: IDatasource<{ item: IStatusPanelItem }>;
  public hasAttributeFilter$: Observable<boolean>;
  public hasAttributeError$: Observable<boolean>;

  public eventCodeShutDown = ETrackingDeviceEventCode.ShutDown;
  public ESignalStatus = ESignalStatus;
  public EPlotIcon = EPlotIcon;
  public EMapContextMenuType = EMapContextMenuType;
  public EPlotType = EPlotType;
  public selectedItemId: number;

  public highlightedVehicles = new Set<number>();
  // same time frame as the one being used in legacy
  private readonly _timeToUpdateStatus = 120000;

  private readonly _vehiclesWithWeakSignalByBatchId = new Map<number, Map<number, Date>>();
  private readonly _updatingVehiclesByBatchId = new Map<number, Set<number>>();
  private readonly _vehicleIdToIndex = new Map<number, number>();
  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private readonly _domHelperService: DomHelperService,
    private readonly _store: Store<IAppState>,
    private readonly _reverseGeocodingService: ReverseGeocodingService,
    private readonly _contextMenuDataLoaderService: ContextMenuDataLoaderService
  ) {}

  public static trackByVehicleFn(index: number, vehicle: IVehiclePlot): any {
    if (isElementNull(vehicle)) {
      return null;
    }
    return `${vehicle.id},${vehicle.ticks},${vehicle.iconClass},${vehicle.isSearchingAddress},
    ${vehicle.speedViolation?.violationSpeed},${vehicle?.driverDisplayName},${vehicle.signalStatus?.iconClass}`;
  }

  public static trackByAssetFn(index: number, asset: IAssetPlot): any {
    if (isElementNull(asset)) {
      return null;
    }
    return `${asset.id},${asset.ticks},${asset.iconClass}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.itemToScrollToId?.firstChange) {
      this._scrollToItem();
      this.selectedItemId = this.itemToScrollTo?.id;
      if (this.selectedItemId > 0) {
        this._store.dispatch(new SelectVehicleIdForDeviceHealth(this.selectedItemId.toString()));
      }
      if (this.isPingMVPEnabled) {
        this._updateSidePanelContent(this.itemToScrollTo?.type);
      }
    }
  }

  public ngOnInit(): void {
    this.isPermissionEnabled = EPermissionStatus.Full;
    this.vehiclesIdsSorted$ = this._store.pipe(select(getVehiclesIdsSorted));
    this.vehicleStatusFilterSelected$ = this._store.pipe(select(getVehicleStatusFilterType));
    this.vehicleFuelTypes$ = this._store.pipe(select(getVehicleFuelTypesFromHierarchy));
    this.userPermissions$ = this._store.pipe(select(getUserPermissions));
    this.userAccountSettings$ = this._store.pipe(select(getUserAccountSettings));
    this.userSettings$ = this._store.pipe(select(getUserSettings));

    this.vehicleStatusInfo$ = this._setupVehicleStatusItems();
    this.vehiclePlotsTimers$ = this._store.pipe(select(getVehiclePlotsTimers));

    this.vehicles$ = this._sortVehiclesById();

    this.vehicles$.pipe(takeUntil(this._unsubscribe$)).subscribe(vehicles => {
      this._clearSignalTooltips();
      this._processVehiclesInBatches(vehicles);
    });

    this.allVehicleDeviceHealthStatus$ = this._store.pipe(select(getAllDevicesHealth));

    this.hasAttributeFilter$ = this._store.pipe(
      select(getFilteredEntityIds),
      map(ids => ids !== null)
    );

    this.hasAttributeError$ = this._store.pipe(select(getAssignmentsError));

    this.statusPanelItems$ = this._sortItemsById();

    this._setupVirtualScrolling();
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public onRetryFilter(): void {
    this._store.dispatch(new RetrySearchAssignments());
  }

  public getVehicleHealthStatus(id: number): Observable<IDeviceHealth> {
    return this.allVehicleDeviceHealthStatus$.pipe(map(items => items.find(x => x.vehicleId == id.toString())));
  }
  public getVehicleIdSelector(id: number): string {
    return `li-vehicle-status-${id}`;
  }

  public getVehiclePlotTimer(vehicleId: number): Observable<number> {
    return this.vehiclePlotsTimers$.pipe(map((timers: IIndexableObject<number>) => timers[vehicleId]));
  }

  public onVehicleMouseEnter(vehicle: IVehiclePlot): void {
    this._store.dispatch(new HighlightMarker(vehicle.id));
  }

  public onVehicleMouseLeave(): void {
    this._store.dispatch(new RemoveMarkerHighlight());
  }

  public onVehicleClick(vehicle: IVehiclePlot): void {
    const params: IZoomToMarkerParams = {
      latLng: vehicle.coordinates as LatLng,
      id: vehicle.id,
      type: vehicle.type,
      title: vehicle.vehicleName
    };

    if (this.selectedItemId !== vehicle.id) {
      this.selectedItemId = vehicle.id;
    }

    this.clicked.emit();

    this._store.dispatch(new SelectVehicleIdForDeviceHealth(vehicle.id.toString()));
    this._store.dispatch(new TryUpdateZoomToMarker(params));
    if (this.isPingMVPEnabled) {
      this._updateSidePanelContent(vehicle.type);
    }
  }

  public onSelectVehiclesClicked(): void {
    this._store.dispatch(new UpdateVehicleDriverPanelActiveTab(EVehicleDriverPanelTabs.VehicleList));
  }

  public openVehicleContextMenuClick($event: MouseEvent, vehiclePlot: IVehiclePlot): void {
    $event.stopPropagation();
    const eventElement = ($event.target as Element).parentNode as HTMLElement;
    const targetElementPosition = this._domHelperService.getBoundingClientRectWithElement(eventElement);
    const margin = targetElementPosition.width + 8;
    const clickPosition: IPoint = {
      x: targetElementPosition.left + margin,
      y: targetElementPosition.top
    };
    const vehicleContextMenuData: IMapContextMenuData<MarkerInfo> = this._getContextMenuData(vehiclePlot, clickPosition);
    this._store.dispatch(new OpenVehicleContextMenu(vehicleContextMenuData));
  }

  public onVehicleRightClick($event: MouseEvent, vehiclePlot: IVehiclePlot): void {
    const clickPosition: IPoint = {
      x: $event.pageX,
      y: $event.pageY
    };
    const vehicleContextMenuData: IMapContextMenuData<MarkerInfo> = this._getContextMenuData(vehiclePlot, clickPosition);
    this._store.dispatch(new OpenVehicleContextMenu(vehicleContextMenuData));
  }

  public getAssetIdSelector(id: number): string {
    return `li-asset-status-${id}`;
  }

  public onAssetClick(asset: IAssetPlot): void {
    const params: IZoomToMarkerParams = {
      latLng: new LatLng(asset.center.Latitude, asset.center.Longitude),
      id: asset.id,
      type: EPlotType.asset,
      title: asset.name
    };

    if (this.selectedItemId !== asset.id) {
      this.selectedItemId = asset.id;
    }

    this.clicked.emit();

    this._store.dispatch(new SelectVehicleIdForDeviceHealth(asset.id.toString()));
    this._store.dispatch(new TryUpdateZoomToMarker(params));

    if (this.isPingMVPEnabled) {
      this._updateSidePanelContent(asset.type);
    }
  }

  public onAssetMouseEnter(asset: IAssetPlot): void {
    this._store.dispatch(new HighlightMarker(asset.id));
  }

  public onAssetMouseLeave(): void {
    this._store.dispatch(new RemoveMarkerHighlight());
  }

  public trackByPanelItem(index: number, item: IStatusPanelItem): any {
    if (isElementNull(item)) {
      return null;
    }
    const isAsset = this._isAsset(item);
    if (isAsset) {
      return VehicleStatusComponent.trackByAssetFn(index, item);
    } else {
      return VehicleStatusComponent.trackByVehicleFn(index, item);
    }
  }

  public openAssetContextMenuClick($event: MouseEvent, assetPlot: IAssetPlot): void {
    $event.stopPropagation();
    const eventElement = ($event.target as Element).parentNode as HTMLElement;
    const targetElementPosition = this._domHelperService.getBoundingClientRectWithElement(eventElement);
    const margin = targetElementPosition.width + 8;
    const clickPosition: IPoint = {
      x: targetElementPosition.left + margin,
      y: targetElementPosition.top
    };
    const assetContextMenuData: IMapContextMenuData<MarkerInfo> = this._getAssetContextMenuData(assetPlot, clickPosition);
    this._store.dispatch(new OpenVehicleContextMenu(assetContextMenuData));
  }

  public onAssetRightClick($event: MouseEvent, assetPlot: IAssetPlot): void {
    const clickPosition: IPoint = {
      x: $event.pageX,
      y: $event.pageY
    };
    const assetContextMenuData: IMapContextMenuData<MarkerInfo> = this._getAssetContextMenuData(assetPlot, clickPosition);
    this._store.dispatch(new OpenVehicleContextMenu(assetContextMenuData));
  }

  private _setupVirtualScrolling(): void {
    this.statusPanelItems = this._initializeDatasource();
    this._setupVirtualScrollingReloadEvents();
    this._setupVirtualScrollingUpdateItemsEvents();
  }

  private _initializeDatasource(): IDatasource<{ item: IStatusPanelItem }> {
    return new Datasource({
      get: (index: number, count: number) =>
        this.statusPanelItems$.pipe(
          take(1),
          map(items => {
            const start = Math.max(0, index);
            const end = Math.min(start + count, items.length);
            // slice the items for the buffer and create a new reference.
            // this is required because of the way updates work (adaper.fix)
            // when we update the items in the buffer, the reference stays the same but we'll update the item property
            // which will force change detection
            return items.slice(start, end).map(i => ({ item: i }));
          }),
          tap(loadedItems => this._reverseGeocodingService.getMissingAddressesOfStatusPanelItems(loadedItems.map(items => items.item)))
        ),
      settings: {
        itemSize: 75, // just a hint for average size
        minIndex: 0,
        startIndex: 0,
        bufferSize: 10
      }
    });
  }

  private _setupVirtualScrollingUpdateItemsEvents(): void {
    const itemsUpdated$ = this.statusPanelItems$.pipe(
      pairwise(),
      filter(([list1, list2]) => list1.length === list2.length)
    );

    itemsUpdated$
      .pipe(
        takeUntil(this._unsubscribe$),
        map(([list1, list2]) => this._getItemsToUpdate(list1, list2)),
        filter(itemsToUpdate => itemsToUpdate.size > 0)
      )
      .subscribe(itemsToUpdate => this._updateVirtualScrollBufferItems(itemsToUpdate));
  }

  private _updateVirtualScrollBufferItems(itemsToUpdate: Map<number, IStatusPanelItem>) {
    const itemsToGetAddress: IStatusPanelItem[] = [];
    this.statusPanelItems.adapter
      .fix({
        updater: ({ $index, data }: { $index: any; data: any }) => {
          if (itemsToUpdate.has($index)) {
            itemsToGetAddress.push(itemsToUpdate.get($index));
            data.item = itemsToUpdate.get($index);
          }
        }
      })
      ?.then(() => this._reverseGeocodingService.getMissingAddressesOfStatusPanelItems(itemsToGetAddress))
      ?.catch((err: any) => {});
  }

  private _getItemsToUpdate(list1: IStatusPanelItem[], list2: IStatusPanelItem[]): Map<number, IStatusPanelItem> {
    const itemsToUpdate = new Map<number, IStatusPanelItem>();
    list1.forEach((item, index) => {
      const isChanged = this.trackByPanelItem(null, item) !== this.trackByPanelItem(null, list2[index]);
      if (isChanged) {
        itemsToUpdate.set(index, list2[index]);
      }
    });
    return itemsToUpdate;
  }

  private _setupVirtualScrollingReloadEvents(): void {
    const itemsAddedOrRemoved$ = this.statusPanelItems$.pipe(
      pairwise(),
      filter(([list1, list2]) => list1.length !== list2.length)
    );

    const tabsSwitched$ = this._store.pipe(
      select(getVehicleDriverPanelActiveTab),
      filter(tab => tab === EVehicleDriverPanelTabs.VehicleStatus)
    );

    // reload list on switch tabs
    combineLatest([itemsAddedOrRemoved$, tabsSwitched$])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.statusPanelItems.adapter.reload()?.catch((err: any) => {});
      });
  }

  private _getContextMenuData(vehiclePlot: IVehiclePlot, clickPosition: IPoint): IMapContextMenuData<MarkerInfo> {
    return this._contextMenuDataLoaderService.getVehicleContextMenuData(vehiclePlot, clickPosition);
  }

  private _getAssetContextMenuData(assetPlot: IAssetPlot, clickPosition: IPoint): IMapContextMenuData<MarkerInfo> {
    return this._contextMenuDataLoaderService.getAssetContextMenuData(assetPlot, clickPosition);
  }

  private _clearSignalTooltips(): void {
    if (!isElementNull(this.normalSignalIconTooltip)) {
      this.normalSignalIconTooltip.hide();
    }
    if (!isElementNull(this.lowSignalIconTooltip)) {
      this.lowSignalIconTooltip.hide();
    }
    if (!isElementNull(this.lostSignalIconTooltip)) {
      this.lostSignalIconTooltip.hide();
    }
  }

  private _processVehiclesInBatches(vehicles: IVehiclePlot[]): void {
    const batchId = Date.now();
    vehicles.forEach(vehicle => {
      if (vehicle.isUpdating) {
        this._setVehicleAsUpdating(batchId, vehicle);
      }

      if (vehicle.signalStatus.iconClass === ESignalStatus.Low) {
        this._setVehicleWithWeakSignal(batchId, vehicle);
      }
    });

    if (this._vehiclesWithWeakSignalByBatchId.has(batchId)) {
      this._setupWeakSignalTimeout(batchId);
    }
    if (this._updatingVehiclesByBatchId.has(batchId)) {
      this._setupUpdatingVehiclesTimeout(batchId, vehicles);
    }
  }

  private _setVehicleAsUpdating(batchId: number, vehicle: IVehiclePlot): void {
    let isAlreadyUpdating = false;
    for (const [, vehicles] of this._updatingVehiclesByBatchId) {
      if (vehicles.has(vehicle.id)) {
        isAlreadyUpdating = true;
        break;
      }
    }

    if (!isAlreadyUpdating) {
      const batchVehicles = this._updatingVehiclesByBatchId.get(batchId);
      if (batchVehicles) {
        batchVehicles.add(vehicle.id);
      } else {
        this._updatingVehiclesByBatchId.set(batchId, new Set([vehicle.id]));
      }

      this.highlightedVehicles.add(vehicle.id);
    }
  }

  private _setVehicleWithWeakSignal(batchId: number, vehicle: IVehiclePlot): void {
    let isAlreadyProcessing = false;
    for (const [, vehicles] of this._vehiclesWithWeakSignalByBatchId) {
      const storedVehicleSignalLastUpdate = vehicles.get(vehicle.id);
      if (storedVehicleSignalLastUpdate) {
        if (vehicle.signalStatus.lastUpdate === storedVehicleSignalLastUpdate) {
          isAlreadyProcessing = true;
          break;
        } else {
          vehicles.delete(vehicle.id);
          break;
        }
      }
    }

    if (!isAlreadyProcessing) {
      const batchVehicles = this._vehiclesWithWeakSignalByBatchId.get(batchId);
      if (batchVehicles) {
        batchVehicles.set(vehicle.id, vehicle.signalStatus.lastUpdate);
      } else {
        this._vehiclesWithWeakSignalByBatchId.set(batchId, new Map([[vehicle.id, vehicle.signalStatus.lastUpdate]]));
      }
    }
  }

  private _setupWeakSignalTimeout(batchId: number): void {
    setTimeout(() => {
      const vehicles = this._vehiclesWithWeakSignalByBatchId.get(batchId);
      const signalUpdatesEventData = this._getSignalUpdates(vehicles);
      this._store.dispatch(new UpdateVehiclesStatus(signalUpdatesEventData));
      this._vehiclesWithWeakSignalByBatchId.delete(batchId);
    }, this._timeToUpdateStatus);
  }

  private _getSignalUpdates(vehicles: Map<number, Date>): ISignalStatusUpdate[] {
    const signalUpdates: ISignalStatusUpdate[] = [];
    const now = Date.now();
    vehicles.forEach((lastUpdate, vehicleId) => {
      const hasWeakSignal = now - +lastUpdate < this._timeToUpdateStatus;
      if (!hasWeakSignal) {
        const signalUpdate: ISignalStatusUpdate = {
          alert: {
            troubleCode: null,
            vehicleId
          }
        };
        signalUpdates.push(signalUpdate);
      }
    });
    return signalUpdates;
  }

  private _setupUpdatingVehiclesTimeout(batchId: number, vehicles: IVehiclePlot[]): void {
    setTimeout(() => {
      const highlightedVehiclesIds = this._updatingVehiclesByBatchId.get(batchId);

      if (isElementNull(highlightedVehiclesIds)) {
        return;
      }

      vehicles.forEach(vehicle => {
        if (highlightedVehiclesIds.has(vehicle.id)) {
          this.highlightedVehicles.delete(vehicle.id);
        }
      });
      this._updatingVehiclesByBatchId.delete(batchId);
    }, 1000);
  }

  private _setupVehicleStatusItems(): Observable<IVehicleStatusInfo> {
    return combineLatest([this.userPermissions$, this.userAccountSettings$, this.userSettings$]).pipe(
      map(([userPermissions, userAccountSettings, userSettings]) => {
        if (userPermissions && userAccountSettings && userSettings) {
          const vehicleStatusItems: IVehicleStatusInfo = {
            showGPSSignalStrength: false,
            showVehicleSpeed: false,
            showSpeedLimits: false,
            speedUnits: userSettings.SpeedUnits,
            userCountry: userSettings.Country,
            speedLimit: userAccountSettings.SpeedLimitOption,
            showHealthStatus: false
          };

          if (userPermissions.showSpeedLimits === this.isPermissionEnabled) {
            vehicleStatusItems.showSpeedLimits = true;
          }

          if (userPermissions.showGPSSignalStrength === this.isPermissionEnabled) {
            vehicleStatusItems.showGPSSignalStrength = true;
          }

          if (userPermissions.showVehicleSpeed === this.isPermissionEnabled) {
            vehicleStatusItems.showVehicleSpeed = true;
          }

          if (userPermissions.manageVehicles === EPermissionStatus.Full) {
            vehicleStatusItems.showHealthStatus = true;
          }

          return vehicleStatusItems;
        }
      })
    );
  }

  private _sortItemsById(): Observable<IStatusPanelItem[]> {
    return combineLatest([
      this.vehiclesIdsSorted$,
      this._store.pipe(select(getStatusPanelPlots)),
      this.vehicleStatusFilterSelected$,
      this.allVehicleDeviceHealthStatus$,
      this._store.pipe(select(getFilteredEntityIds))
    ]).pipe(
      map(([sortedVehiclesIds, panelItems, filterToApply, devicesHealth, filteredEntityIds]) => {
        if (isArrayNullOrEmpty(sortedVehiclesIds) || isArrayNullOrEmpty(panelItems)) {
          return panelItems;
        }

        const items = filterByEntityIds(panelItems, filteredEntityIds);

        return this._applySortingAndFilter(items, filterToApply, sortedVehiclesIds, devicesHealth);
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  private _sortVehiclesById(): Observable<IVehiclePlot[]> {
    return combineLatest([
      this.vehiclesIdsSorted$,
      this._store.pipe(select(getVehiclePlots)),
      this.vehicleStatusFilterSelected$,
      this.allVehicleDeviceHealthStatus$,
      this._store.pipe(select(getFilteredEntityIds))
    ]).pipe(
      map(([sortedVehiclesIds, panelItems, filterToApply, devicesHealth, filteredEntityIds]) => {
        if (isArrayNullOrEmpty(sortedVehiclesIds) || isArrayNullOrEmpty(panelItems)) {
          return panelItems;
        }

        const items = filterByEntityIds(panelItems, filteredEntityIds);

        return this._applySortingAndFilter(items as IStatusPanelItem[], filterToApply, sortedVehiclesIds, devicesHealth);
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  private _applySortingAndFilter(
    panelItems: IStatusPanelItem[],
    filterToApply: EStatusFilterType,
    sortedVehiclesIds: number[],
    devicesHealth: IDeviceHealth[]
  ): IStatusPanelItem[] {
    const idToItemMap = new Map<number, IStatusPanelItem>();
    // iterate through the panel items and create a map that contains the filtered items with id as the key
    panelItems.forEach(vehicle => {
      const deviceHealthStatus = devicesHealth.find(x => x.vehicleId == vehicle.id.toString());

      // Device error filters
      if (deviceHealthStatus && Object.values(EDeviceErrorType).includes(filterToApply as string | EDeviceErrorType)) {
        vehicle.errorStatuses = [];
        if (deviceHealthStatus?.cameraHealth?.length > 0) vehicle.errorStatuses.push(EDeviceErrorType.CameraError);
        if (deviceHealthStatus?.componentHealth?.length > 0) vehicle.errorStatuses.push(EDeviceErrorType.TrackerError);

        if (
          filterToApply == EDeviceErrorType.All ||
          (vehicle.errorStatuses.includes(filterToApply as EDeviceErrorType) && deviceHealthStatus?.healthStatus.toUpperCase() != 'OPTIMAL')
        ) {
          idToItemMap.set(vehicle.id, vehicle);
        }
      }

      // Status filters
      if (filterToApply === EVehicleStatusType.All || statusToFilterableStatusMap.get(vehicle.status)?.has(filterToApply)) {
        idToItemMap.set(vehicle.id, vehicle);
      }
    });

    // iterate through the sorted ids and add items only if they are found in the map
    let count = 0;
    const sorted: IStatusPanelItem[] = [];
    sortedVehiclesIds.forEach(id => {
      if (idToItemMap.has(id)) {
        sorted.push(idToItemMap.get(id));
        this._vehicleIdToIndex.set(id, count++);
      }
    });
    return sorted;
  }

  private _isAsset(item: IStatusPanelItem): boolean {
    return !isElementNull(item.center);
  }

  private _scrollToItem() {
    this.statusPanelItems?.adapter.reload(this._vehicleIdToIndex.get(this.itemToScrollTo?.id));
  }

  private _updateSidePanelContent(type: EPlotType) {
    if (this.selectedItemId) {
      this._store.dispatch(new SetSidePanelSelectedItem({ id: this.selectedItemId, type: type } as IScrollItem));
    }
  }
}
